import { SDK } from '@ringcentral/sdk'
import axios from 'axios'

class RingCentralClient {
  static instance
  constructor(cachePrefix) {
    const {
      REACT_APP_RINGCENTRALOAUTH_REDIRECT_URL,
      REACT_APP_RINGCENTRALOAUTH_CLIENT_ID,
      REACT_APP_RINGCENTRALOAUTH_CLIENT_SECRET,
    } = process.env
    // Used to login crm user and to call out customer
    this.rc_client_oauth = new SDK({
      server: SDK.server.production,
      clientId: REACT_APP_RINGCENTRALOAUTH_CLIENT_ID,
      clientSecret: REACT_APP_RINGCENTRALOAUTH_CLIENT_SECRET,
      redirectUri: decodeURIComponent(REACT_APP_RINGCENTRALOAUTH_REDIRECT_URL),
      cachePrefix,
    })
    this.platform_oauth = this.rc_client_oauth.platform()
  }

  static async getInstance(cachePrefix) {
    if (!this.instance) {
      this.instance = new RingCentralClient(cachePrefix)

      const authData = await this.instance.platform_oauth.auth().data()
      console.log(authData)
    }

    return this.instance
  }

  async makeCall({ caller, extension, recipient, user }) {
    try {
      await this.platform_oauth.auth().setData({
        access_token: user.accessToken,
        refresh_token: user.refreshToken,
        expire_time: +user.expireTime,
        expires_in: +user.expiresIn,
        refresh_token_expire_time: +user.refreshTokenExpireTime,
        refresh_token_expires_in: +user.refreshTokenExpiresIn,
      })

      const isRefreshTokenValid = await this.platform_oauth.auth().refreshTokenValid()

      if (!isRefreshTokenValid) {
        await axios.post('/click-to-call/log', {
          data: {
            user,
            caller,
            recipient,
          },
          additionalMessage: 'refreshTokenExpired',
          error: 'Your ring central session expired, please login again from your profile!',
        })

        return { error: true, errorMessage: 'Your ring central session expired, please login again from your profile!' }
      }

      let refreshedAccessToken = {}

      const accessTokenRefreshDate = +user.expireTime - 6 * 60 * 1000 // - 6min

      if (accessTokenRefreshDate < Date.now()) {
        refreshedAccessToken = await this.refreshAccessToken(user, 'customRefresh')

        if (!Object.keys(refreshedAccessToken).length) {
          return {
            error: true,
            errorMessage: 'Your ring central session expired, please login again from your profile!',
          }
        }
      }

      const isAccessTokenValid = await this.platform_oauth.auth().accessTokenValid()

      if (!isAccessTokenValid) {
        refreshedAccessToken = await this.refreshAccessToken(user, 'rcRefresh')

        if (!Object.keys(refreshedAccessToken).length) {
          return {
            error: true,
            errorMessage: 'Your ring central session expired, please login again from your profile!',
          }
        }
      }

      let call

      if (user.useExtensionToMakeCalls) {
        call = await this.platform_oauth.send({
          method: 'POST',
          url: '/restapi/v1.0/account/~/extension/~/ring-out',
          body: {
            from: { phoneNumber: '+1' + process.env.REACT_APP_RINGCENTRAL_MAIN_COMPANY_NUMBER + '*' + extension },
            to: { phoneNumber: recipient },
            // callerId: { phoneNumber: '+1' + caller },
            playPrompt: true,
          },
        })
      } else {
        call = await this.platform_oauth.send({
          method: 'POST',
          url: '/restapi/v1.0/account/~/extension/~/ring-out',
          body: {
            from: { phoneNumber: '+1' + caller },
            to: { phoneNumber: recipient },
            callerId: { phoneNumber: '+1' + caller },
            playPrompt: false,
          },
        })
      }

      const callResponse = await call.json()

      return { error: false, callResponse, refreshedAccessToken }
    } catch (error) {
      await axios.post('/click-to-call/log', {
        data: {
          user,
          caller,
          recipient,
        },
        additionalMessage: 'makeCall',
        error: (error.stack ? error.stack : error.message).slice(0, 1000) || 'No Error Message',
      })
      return { error: true, errorMessage: error.message }
    }
  }

  /**
   * Send a P2P SMS from the user's own RingCentral number — same auth path as
   * makeCall (RingOut): the browser holds the user's OAuth token, so the message
   * goes out AS that user, exactly like the native RC Text app. P2P (not A2P), so
   * no 10DLC/TCR registration and no "[from] value is invalid".
   *
   * @param from  user's own 10-digit number (E.164 +1 added here)
   * @param to    recipient 10-digit number
   * @param user  rcSession tokens (accessToken/refreshToken/expireTime…)
   */
  async sendSMS({ from, to, text, user }) {
    try {
      await this.platform_oauth.auth().setData({
        access_token: user.accessToken,
        refresh_token: user.refreshToken,
        expire_time: +user.expireTime,
        expires_in: +user.expiresIn,
        refresh_token_expire_time: +user.refreshTokenExpireTime,
        refresh_token_expires_in: +user.refreshTokenExpiresIn,
      })

      const isRefreshTokenValid = await this.platform_oauth.auth().refreshTokenValid()
      if (!isRefreshTokenValid) {
        return { error: true, errorMessage: 'Your RingCentral session expired, please login again from your profile!' }
      }

      let refreshedAccessToken = {}

      const accessTokenRefreshDate = +user.expireTime - 6 * 60 * 1000 // - 6min
      if (accessTokenRefreshDate < Date.now()) {
        refreshedAccessToken = await this.refreshAccessToken(user, 'customRefresh')
        if (!Object.keys(refreshedAccessToken).length) {
          return {
            error: true,
            errorMessage: 'Your RingCentral session expired, please login again from your profile!',
          }
        }
      }

      const isAccessTokenValid = await this.platform_oauth.auth().accessTokenValid()
      if (!isAccessTokenValid) {
        refreshedAccessToken = await this.refreshAccessToken(user, 'rcRefresh')
        if (!Object.keys(refreshedAccessToken).length) {
          return {
            error: true,
            errorMessage: 'Your RingCentral session expired, please login again from your profile!',
          }
        }
      }

      const res = await this.platform_oauth.send({
        method: 'POST',
        url: '/restapi/v1.0/account/~/extension/~/sms',
        body: {
          from: { phoneNumber: '+1' + from },
          to: [{ phoneNumber: '+1' + to }],
          text,
        },
      })

      const smsResponse = await res.json()
      return { error: false, smsResponse, refreshedAccessToken }
    } catch (error) {
      // RC returns a structured error body; surface its message when present.
      let errorMessage = error.message
      try {
        if (error.response) {
          const body = await error.response.clone().json()
          errorMessage = body.message || (body.errors && body.errors[0] && body.errors[0].message) || errorMessage
        }
      } catch (_) {
        /* not a JSON body */
      }
      return { error: true, errorMessage }
    }
  }

  /**
   * Register a message-store (instant SMS) subscription for THIS user's own
   * extension, using their OAuth token (platform_oauth must already be logged in
   * — call right after getToken). message-store has no account-wide wildcard, so
   * each user subscribes for their own extension; this is what makes P2P inbound
   * (native RC Text replies) reach the CRM per user. Delivery goes to our
   * /api/v2/rc/sms webhook. Returns the subscription id, or null on failure.
   */
  async registerSmsSubscription({ address }) {
    try {
      const res = await this.platform_oauth.post('/restapi/v1.0/subscription', {
        eventFilters: ['/restapi/v1.0/account/~/extension/~/message-store/instant?type=SMS'],
        expiresIn: 315360000,
        deliveryMode: { transportType: 'WebHook', address },
      })
      const data = await res.json()
      return data && data.id ? data.id : null
    } catch (error) {
      console.log('registerSmsSubscription error =>>>', error.message)
      return null
    }
  }

  async deleteSubscription(subscriptionId) {
    if (!subscriptionId) return
    try {
      await this.platform_oauth.delete(`/restapi/v1.0/subscription/${subscriptionId}`)
    } catch (error) {
      // Best effort — a stale/already-deleted subscription is harmless.
      console.log('deleteSubscription error =>>>', error.message)
    }
  }

  oauthLogin(state) {
    return this.platform_oauth.loginUrl({ state })
  }

  async getToken(code) {
    try {
      await this.platform_oauth.login({
        code,
        redirect_uri: process.env.RINGCENTRALOAUTH_REDIRECT_URL,
      })
      const authData = await this.platform_oauth.auth().data()

      return authData
    } catch (err) {
      console.log('getToken err =>>> ', err)
      return {}
    }
  }

  async refreshAccessToken(user, type) {
    try {
      await axios.post('/click-to-call/log-info', {
        data: {
          user,
          type,
        },
        additionalMessage: 'refreshAccessToken',
      })
      // await this.platform_oauth.auth().cancelAccessToken()
      const res = await this.platform_oauth.refresh()
      const data = await res.json()
      const expire_time = new Date(Date.now() + data.expires_in * 1000).getTime()
      // RC's refresh response carries refresh_token_expires_in but NOT refresh_token_expire_time;
      // compute it so the rotated refresh token is persisted with a valid expiry (otherwise the
      // save sends `undefined` and the whole update fails, leaving a stale/dead refresh token).
      const refresh_token_expire_time = data.refresh_token_expires_in
        ? new Date(Date.now() + data.refresh_token_expires_in * 1000).getTime()
        : undefined

      return { ...data, expire_time, refresh_token_expire_time }
    } catch (error) {
      await axios.post('/click-to-call/log', {
        data: {
          user,
          type,
        },
        additionalMessage: 'refreshAccessToken',
        error: (error.stack ? error.stack : error.message).slice(0, 1000) || 'No Error Message',
      })
      return {}
    }
  }
}

export { RingCentralClient }
